import {ReactNode, useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';

// Bottom sheet (DESIGN.md §5): ground fill, top radius 26, grab handle, scrim. Closes on scrim
// tap and Escape, locks body scroll while open, returns focus to where it was.
//
// A sheet often holds a form and is often opened from inside another one (+ New place on the
// tell form). Two things keep the sheet's form to itself:
// - It renders into document.body. Browsers stop a nested <form>'s submit event at the outer
//   form, so it never reached React's listener on #root: nothing called preventDefault and the
//   page reloaded as a GET submit (David's lost places, 23 Oct 2026).
// - It stops React's submit at its edge. React bubbles synthetic events through portals by the
//   component tree, so without this the form behind the sheet would be saved as well.
type Props = {
    open: boolean;
    onClose: () => void;
    title?: ReactNode;
    children: ReactNode;
    /** Keep the sheet open while a request is in flight. */
    busy?: boolean;
};

export default function Sheet({open, onClose, title, children, busy = false}: Props) {
    const panel = useRef<HTMLDivElement>(null);
    const opener = useRef<Element | null>(null);
    // Read through refs so the effect runs on open and close only: callers pass a fresh
    // onClose every render, and re-running would pull focus back to the first field.
    const onCloseRef = useRef(onClose);
    const busyRef = useRef(busy);
    onCloseRef.current = onClose;
    busyRef.current = busy;

    useEffect(() => {
        if (!open) return;
        opener.current = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !busyRef.current) onCloseRef.current();
        };
        document.addEventListener('keydown', onKey);
        window.setTimeout(() => panel.current?.querySelector<HTMLElement>('[autofocus], input, button')?.focus(), 0);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKey);
            (opener.current as HTMLElement | null)?.focus?.();
        };
    }, [open]);

    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 z-30 flex items-end justify-center" role="presentation" onSubmit={e => e.stopPropagation()}>
            <div className="scrim absolute inset-0" onClick={() => !busy && onClose()} />
            <div
                ref={panel}
                role="dialog"
                aria-modal="true"
                className="sheet settle-in relative w-full max-w-[560px] max-h-[88dvh] overflow-y-auto px-4 pb-[max(20px,env(safe-area-inset-bottom))] pt-2"
            >
                <div aria-hidden className="mx-auto mb-3 h-[5px] w-10 rounded-pill bg-line" />
                {title && <h2 className="m-0 mb-4 text-xl">{title}</h2>}
                {children}
            </div>
        </div>,
        document.body
    );
}
